import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface UserPreferences {
  id?: string;
  preferred_currency: string;
  time_zone: string;
  notifications_email: boolean;
  notifications_sms: boolean;
  notifications_push: boolean;
  profile_visibility: 'public' | 'private' | 'contacts';
  show_online_status: boolean;
  compact_view: boolean;
  theme: 'light' | 'dark' | 'system';
  region: string;
  // Values come from FontSizeContext's A- / A / A+ / A++ cycle, which is the
  // only writer of this column. The union previously read
  // 'small' | 'normal' | 'large' | 'xlarge', which matched nothing: all 297
  // live rows held 'normal' because nothing had ever written the column.
  font_size: 'compact' | 'normal' | 'large' | 'xl';
  /** Simple Mode (3-tap interface). Owned by SimpleModeContext. */
  simple_mode: boolean;
  high_contrast: boolean;
  reduced_motion: boolean;
  screen_reader_optimized: boolean;
  dyslexic_font: boolean;
  behavior_data: Record<string, any>;
}

const defaultPreferences: UserPreferences = {
  preferred_currency: 'PKR',
  time_zone: 'Asia/Karachi',
  notifications_email: true,
  notifications_sms: false,
  notifications_push: true,
  profile_visibility: 'public',
  show_online_status: true,
  compact_view: false,
  theme: 'light',
  region: 'karachi',
  font_size: 'normal',
  simple_mode: false,
  high_contrast: false,
  reduced_motion: false,
  screen_reader_optimized: false,
  dyslexic_font: false,
  behavior_data: {},
};

export function useUserPreferences() {
  const { user, isAuthenticated } = useUser();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load preferences from database
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      // For unauthenticated users, try to load from localStorage as fallback.
      // Use the same key as the canonical ThemeProvider so they stay in sync.
      try {
        const savedTheme = localStorage.getItem('behtari-theme') as 'light' | 'dark' | 'system' | null;
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          setPreferences(prev => ({ ...prev, theme: savedTheme }));
        }
      } catch {
        // Ignore localStorage errors
      }
      setIsLoading(false);
      return;
    }

    const loadPreferences = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.error('Failed to load preferences:', fetchError);
          setError(fetchError.message);
          return;
        }

        if (data) {
          setPreferences({
            id: data.id,
            preferred_currency: data.preferred_currency || 'PKR',
            time_zone: data.time_zone || 'Asia/Karachi',
            notifications_email: data.notifications_email ?? true,
            notifications_sms: data.notifications_sms ?? false,
            notifications_push: data.notifications_push ?? true,
            profile_visibility: (data.profile_visibility as 'public' | 'private' | 'contacts') || 'public',
            show_online_status: data.show_online_status ?? true,
            compact_view: data.compact_view ?? false,
            theme: (data.theme as 'light' | 'dark' | 'system') || 'light',
            region: data.region || 'karachi',
            font_size: (data.font_size as 'compact' | 'normal' | 'large' | 'xl') || 'normal',
            simple_mode: data.simple_mode ?? false,
            high_contrast: data.high_contrast ?? false,
            reduced_motion: data.reduced_motion ?? false,
            screen_reader_optimized: data.screen_reader_optimized ?? false,
            dyslexic_font: data.dyslexic_font ?? false,
            behavior_data: (data.behavior_data as Record<string, any>) || {},
          });
          // NOTE: Theme is applied by the canonical ThemeProvider via
          // ThemePreferenceSync. Do not mutate <html class> here — it would
          // race with ThemeProvider and revert the user's selection.
        } else {
          // Create preferences if they don't exist
          const { error: insertError } = await supabase
            .from('user_preferences')
            .insert({ user_id: user.id });

          if (insertError) {
            console.error('Failed to create preferences:', insertError);
          }
        }
      } catch (err) {
        console.error('Error loading preferences:', err);
        setError('Failed to load preferences');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [isAuthenticated, user?.id]);

  // Update preferences in database
  const updatePreferences = useCallback(async (updates: Partial<UserPreferences>) => {
    const newPreferences = { ...preferences, ...updates };
    setPreferences(newPreferences);

    // NOTE: Theme is owned by the canonical ThemeProvider — do NOT toggle the
    // 'dark' class here, it would race with ThemeProvider's effect and revert.

    // Apply reduced motion
    if (updates.reduced_motion !== undefined) {
      document.documentElement.classList.toggle('reduce-motion', updates.reduced_motion);
    }

    // Apply high contrast
    if (updates.high_contrast !== undefined) {
      document.documentElement.classList.toggle('high-contrast', updates.high_contrast);
    }

    if (!isAuthenticated || !user?.id) {
      // For unauthenticated users, save to localStorage under the canonical key.
      try {
        if (updates.theme) {
          localStorage.setItem('behtari-theme', updates.theme);
        }
      } catch {
        // Ignore localStorage errors
      }
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('user_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) {
        console.error('Failed to update preferences:', updateError);
        setError(updateError.message);
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError('Failed to update preferences');
    }
  }, [preferences, isAuthenticated, user?.id]);

  // Update behavior data (for analytics)
  const trackBehavior = useCallback(async (eventType: string, data: Record<string, any>) => {
    if (!isAuthenticated || !user?.id) return;

    const newBehaviorData = {
      ...preferences.behavior_data,
      [eventType]: {
        ...(preferences.behavior_data[eventType] || {}),
        ...data,
        lastUpdated: new Date().toISOString(),
      },
    };

    setPreferences(prev => ({ ...prev, behavior_data: newBehaviorData }));

    try {
      await supabase
        .from('user_preferences')
        .update({ behavior_data: newBehaviorData })
        .eq('user_id', user.id);
    } catch (err) {
      console.error('Error tracking behavior:', err);
    }
  }, [preferences.behavior_data, isAuthenticated, user?.id]);

  return {
    preferences,
    isLoading,
    error,
    updatePreferences,
    trackBehavior,
  };
}
